import React, { useState, useEffect } from "react";
import { useProfile } from "@/hooks/useProfile";
import { ProfileHeader } from "@/components/profile/ProfileHeader";
import { ProfileForm } from "@/components/profile/ProfileForm";
import { BasicInfoCard } from "@/components/profile/BasicInfoCard";
import { ImagesCard } from "@/components/profile/ImagesCard";
import { SocialAchievementsCard } from "@/components/profile/SocialAchievementsCard";
import { Button } from "@/components/ui/button";
import { ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Achievement {
  id: string;
  title: string;
  description?: string;
}

const Perfil = () => {
  const { profile, mentorInfo, loading, updateMentorInfo, uploadImage, removeImage } = useProfile();
  const { toast } = useToast();
  
  // Estados para campos do formulário
  const [nickname, setNickname] = useState("");
  const [preferredGame, setPreferredGame] = useState("");
  const [biography, setBiography] = useState<string | null>(null);
  const [slug, setSlug] = useState<string | null>(null);
  const [youtubeUrl, setYoutubeUrl] = useState<string | null>(null);
  const [instagramUrl, setInstagramUrl] = useState<string | null>(null);
  const [discordUrl, setDiscordUrl] = useState<string | null>(null);
  const [twitchUrl, setTwitchUrl] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null); // 🎯 Campo para vídeo da página pública
  const [achievements, setAchievements] = useState<Achievement[]>([]);

  // Atualiza os estados quando os dados do perfil são carregados
  useEffect(() => {
    if (profile) {
      setNickname(profile.nickname || "");
      setPreferredGame(profile.preferred_game || "");
      setSlug(profile.slug || null);
    }
    
    if (mentorInfo) {
      console.log('🔄 [Perfil] Carregando dados do mentorInfo:', JSON.stringify({
        id: mentorInfo.id,
        bio: mentorInfo.bio,
        biography: mentorInfo.biography,
        youtube_url: mentorInfo.youtube_url,
        instagram_url: mentorInfo.instagram_url,
        discord_url: mentorInfo.discord_url,
        twitch_url: mentorInfo.twitch_url,
        achievements: mentorInfo.achievements
      }, null, 2));
      console.log('🔗 [Perfil] URLs sociais do mentorInfo:', JSON.stringify({
        youtube_url: mentorInfo.youtube_url,
        instagram_url: mentorInfo.instagram_url,
        discord_url: mentorInfo.discord_url,
        twitch_url: mentorInfo.twitch_url
      }, null, 2));
      
      setBiography(mentorInfo.biography || mentorInfo.bio || "");
      
      const youtube = mentorInfo.youtube_url || "";
      const instagram = mentorInfo.instagram_url || "";
      const discord = mentorInfo.discord_url || "";
      const twitch = mentorInfo.twitch_url || "";
      const video = mentorInfo.video_url || ""; // 🎯 Campo para vídeo da página pública
      
      setYoutubeUrl(youtube);
      setInstagramUrl(instagram);
      setDiscordUrl(discord);
      setTwitchUrl(twitch);
      setVideoUrl(video);
      setAchievements(mentorInfo.achievements || []);
      
      console.log('✅ [Perfil] Estados atualizados com valores específicos:', JSON.stringify({
        youtubeUrl: youtube,
        instagramUrl: instagram,
        discordUrl: discord,
        twitchUrl: twitch
      }, null, 2));
    }
  }, [profile, mentorInfo]);

  // Handler para atualizar slug
  const handleSlugChange = async (newSlug: string | null) => {
    if (!profile?.id) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ slug: newSlug })
        .eq('id', profile.id);

      if (error) {
        console.error('Erro ao atualizar slug:', error);
        toast({
          title: "Erro ao atualizar URL",
          description: "Não foi possível atualizar sua URL personalizada.",
          variant: "destructive",
        });
        return;
      }

      setSlug(newSlug);
      toast({
        title: "URL atualizada!",
        description: "Sua URL personalizada foi salva com sucesso.",
      });
    } catch (error) {
      console.error('Erro ao atualizar slug:', error);
      toast({
        title: "Erro ao atualizar URL",
        description: "Ocorreu um erro inesperado.",
        variant: "destructive",
      });
    }
  };

  // Handlers para upload de imagens
  const handleProfileImageUpload = async (file: File) => {
    await uploadImage(file, 'profile');
  };
  
  const handleBannerImageUpload = async (file: File) => {
    await uploadImage(file, 'banner');
  };
  
  const handleRemoveProfileImage = async () => {
    await removeImage('profile');
  };
  
  const handleRemoveBannerImage = async () => {
    await removeImage('banner');
  };

  // Handler para salvar o formulário
  const handleSubmit = async () => {
    console.log('🚀 [Perfil] ===== INÍCIO DO SUBMIT =====');
    console.log('🔄 [Perfil] Iniciando submit do formulário');
    console.log('📝 [Perfil] Valores atuais dos campos sociais:', JSON.stringify({
      youtubeUrl: youtubeUrl,
      instagramUrl: instagramUrl, 
      discordUrl: discordUrl,
      twitchUrl: twitchUrl
    }, null, 2));
    console.log('📝 [Perfil] Dados completos para envio:', JSON.stringify({
      nickname,
      preferred_game: preferredGame,
      biography,
      youtube_url: youtubeUrl,
      instagram_url: instagramUrl,
      discord_url: discordUrl,
      twitch_url: twitchUrl,
      achievements
    }, null, 2));
    
    try {
      await updateMentorInfo({
        nickname,
        preferred_game: preferredGame,
        biography,
        youtube_url: youtubeUrl,
        instagram_url: instagramUrl,
        discord_url: discordUrl,
        twitch_url: twitchUrl,
        video_url: videoUrl, // 🎯 Campo para vídeo da página pública
        achievements
      });
      console.log('✅ [Perfil] Submit concluído com sucesso');
    } catch (error) {
      console.error('❌ [Perfil] Erro no submit:', error);
      throw error;
    }
  };

  return (
    <div className="space-y-6 pb-10">
      <ProfileHeader loading={loading && !profile && !mentorInfo} />

      {/* Botão "Ir para meu Perfil" */}
      {profile?.slug && (
        <div className="flex justify-end">
          <Button
            onClick={() => window.open(`/mentores/${profile.slug}`, '_blank')}
            variant="outline"
            className="bg-[#1A2233] border-[#2D3748] text-white hover:bg-[#242C37] flex items-center gap-2"
          >
            <ExternalLink className="h-4 w-4" />
            Ir para meu Perfil
          </Button>
        </div>
      )}

      {!(loading && !profile && !mentorInfo) && (
        <ProfileForm 
          profile={profile}
          mentorInfo={mentorInfo}
          loading={loading}
          onSubmit={handleSubmit}
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <BasicInfoCard
              nickname={nickname}
              preferredGame={preferredGame}
              biography={biography}
              slug={slug}
              profileId={profile?.id}
              videoUrl={videoUrl}
              onNicknameChange={setNickname}
              onPreferredGameChange={setPreferredGame}
              onBiographyChange={setBiography}
              onSlugChange={handleSlugChange}
              onVideoUrlChange={setVideoUrl}
            />

            <ImagesCard
              profileImageUrl={mentorInfo?.image_url || null}
              coverImageUrl={mentorInfo?.cover_url || null}
              onProfileImageUpload={handleProfileImageUpload}
              onCoverImageUpload={handleBannerImageUpload}
              onProfileImageRemove={handleRemoveProfileImage}
              onCoverImageRemove={handleRemoveBannerImage}
              showCoverImage={true}
            />

            <SocialAchievementsCard
              youtubeUrl={youtubeUrl}
              instagramUrl={instagramUrl}
              discordUrl={discordUrl}
              twitchUrl={twitchUrl}
              achievements={achievements}
              onYoutubeChange={setYoutubeUrl}
              onInstagramChange={setInstagramUrl}
              onDiscordChange={setDiscordUrl}
              onTwitchChange={setTwitchUrl}
              onAchievementsChange={setAchievements}
            />
          </div>
        </ProfileForm>
      )}
    </div>
  );
};

export default Perfil;
